// TypeScript contracts for the Tenants page (KCC).
// API-aligned shapes mirror the backend tenant record; UI-only fields are
// computed in the transformer and never sent back to the API.

export enum TenantStatus {
	Active = "active",
	Expiring = "expiring",
	Deactivated = "deactivated",
	Decommissioned = "decommissioned",
	Purged = "purged",
}

// Which audience a deactivation blocks (FR-3).
export type DeactivationScope = "client" | "zs" | "both";

export enum TenantType {
	Kural = "kural",
	PXB = "pxb",
}

// Which environment the instance is provisioned for (collected at onboarding,
// default prod). Backend backfills all pre-existing tenants as "prod".
export type TenantEnvironment = "prod" | "uat";

// Kural modules a tenant can enable (onboarding input, editable in Overview).
// Keys mirror the backend's ModuleKey Literal exactly — keep in sync.
export type TenantModule =
	| "qual_market_research"
	| "reports"
	| "quant_market_research"
	| "research_notes"
	| "web_boards";

// Status filter pills on the list screen ("all" shows everything).
export type TenantStatusFilter = TenantStatus | "all";

// Point of contact for a tenant (CST POC) — kept minimal for the avatar cell.
export interface ITenantContact {
	name: string;
	email?: string;
}

// An admin user to be granted access on the new instance (FR-4).
export interface IAdminUser {
	name?: string;
	email?: string;
}

// ── Expiry / purge reminders (FR-6) ───────────────────────────────────────────
// KCC stores these thresholds; the Kural app/scheduler sends the notifications.
export type ReminderSeverity = "info" | "warning" | "critical";

export interface IReminderThreshold {
	id: string;
	label: string;
	// Machine-executable trigger: email fires N days before the expiry/purge
	// date (required since reminders became functional, 2026-08-18).
	days: number;
	severity: ReminderSeverity;
	enabled: boolean;
}

export interface ITenantReminders {
	beforeExpiry: IReminderThreshold[];
	beforePurge: IReminderThreshold[];
}

// Tenant-wide AI spend cap (FR-11). Scope determines whether the cap applies to
// the whole tenant or per-profile. null amounts = no cap for that period.
export interface IAiTotalLimit {
	scope: "tenant" | "profile";
	daily_amount: number | null;
	weekly_amount: number | null;
	on_breach: "warn" | "restrict";
}

// Tenant record as returned by the backend (GET /tenants).
export interface ITenant {
	id: string;
	tenant_code: string;
	tenant_name: string;
	tenant_type: TenantType;
	environment: TenantEnvironment;
	// Enabled modules (legacy rows without the column come back as all five).
	modules: TenantModule[];
	// Free-text onboarding asks (SSO, white-labelling, ETA…); create-time only.
	additional_requirements?: string | null;
	status: TenantStatus;
	// Which audience is blocked while deactivated (null unless status = deactivated).
	deactivated_scope?: DeactivationScope | null;
	// Resolved per-audience access flags (server-derived; main app reads these).
	client_access_active?: boolean;
	zs_access_active?: boolean;
	expiry_date: string | null; // ISO date, null once decommissioned/purged
	// When the tenant was onboarded (ISO date). Editable in Overview — seeded
	// values came from k8s namespace dates, which are approximations.
	onboarded_at: string | null;
	purge_in_days: number | null; // reserved; KCC tracks decommission, no app clock
	// Nullable on the backend (blank edits normalise to null) — treating these
	// as always-strings crashed the Tenants search (arch review P0-1).
	project_code: string | null;
	// Live instance URL. Derived from tenant_code on onboard
	// (https://<code>.kural.zsservices.com/) but editable in Overview.
	tenant_url?: string | null;
	// Deployed bundle — from the deployment pipeline's S3 feed only (view-only,
	// never operator-set). Both null ⇒ the tenant is not live yet.
	bundle_version: string | null;
	bundle_released_at: string | null; // ISO datetime (UTC)
	cst_poc: ITenantContact | null;
	// KST (Kural Support Team) POC — shown wherever the CST POC is.
	kst_poc: ITenantContact | null;
	admin_users?: IAdminUser[];
	reminders?: ITenantReminders;
	ai_total_limit?: IAiTotalLimit | null;
	// Budget / promised cover (FR-14, simplified 2026-09-10): one cover amount
	// for one explicit [cover_start, cover_end) window — CST updates the three
	// values as contracts change. Utilization fields are computed by the daily
	// job (and after a budget PATCH) — never edited directly.
	promised_cover?: number | null;
	cover_start?: string | null; // ISO date
	cover_end?: string | null; // ISO date, exclusive
	utilization_alert_pct?: number | null; // null = server default (70)
	utilization_pct?: number | null;
	utilization_amount?: number | null;
	utilization_computed_at?: string | null;
}

// Dashboard counters shown as stat cards above the table.
export interface ITenantStats {
	active: number;
	expiring: number; // expiring within 30 days
	decommissioned: number;
	total: number;
}

// Onboard-tenant request payload (POST /tenants). Admin users granted access
// are a fixed org-level set on the backend, not part of this payload.
export interface ITenantCreatePayload {
	// Optional at onboarding (operators rarely have it up front): omitted means
	// the backend auto-generates a unique slug from the display name. Editable
	// later in Overview.
	tenant_code?: string;
	tenant_type: TenantType;
	// prod | uat only; the form defaults to prod.
	environment: TenantEnvironment;
	// At least one module must be selected (no pre-selection — explicit choice).
	modules: TenantModule[];
	// Optional free text — included in the provisioning email.
	additional_requirements?: string;
	tenant_name: string;
	// CST POC is optional (product decision 2026-08-14); omitted when blank.
	cst_poc_name?: string;
	cst_poc_email?: string;
	// KST (Kural Support Team) POC — the mandatory contact.
	kst_poc_name: string;
	kst_poc_email: string;
	project_code: string;
	// NOTE: no bundle_version — the deployed version is owned by the deployment
	// pipeline's S3 feed (2026-08-19); operators can view it but never set it.
	expiry_date: string | null;
	// Structured client-side from the tenant code on onboard; omitted when the
	// code is blank (the backend derives it from the generated code).
	tenant_url?: string;
}

export interface IAIHealthComparison {
	count_delta: number;
	rate_delta_pct_points: number | null;
	count_ratio: number;
	regression: boolean;
}

export interface IAIHealthServiceAvailability {
	service_name: string;
	status: "ok" | "errors" | "down" | "no_activity" | "unknown";
	current: {
		ai_traces: number;
		rooted_ai_traces: number;
		failed_roots: number;
		failure_rate_pct: number | null;
	};
	previous: {
		ai_traces: number;
		rooted_ai_traces: number;
		failed_roots: number;
		failure_rate_pct: number | null;
	};
	comparison: IAIHealthComparison;
}

export interface IAIHealthChildError {
	service_name: string;
	model: string | null;
	warning: boolean;
	current: {
		successful_roots: number;
		affected_roots: number;
		affected_root_rate_pct: number | null;
		child_error_records: number;
	};
	previous: {
		successful_roots: number;
		affected_roots: number;
		affected_root_rate_pct: number | null;
		child_error_records: number;
	};
	comparison: IAIHealthComparison;
}

export interface IAIHealthModelOperation {
	service_name: string;
	model: string | null;
	operation_name: string | null;
	current: {
		model_calls: number;
		failed_model_calls: number;
		failure_rate_pct: number | null;
		incomplete_model_calls: number;
		incomplete_rate_pct: number | null;
		max_incomplete_duration_seconds: number | null;
	};
	previous: {
		model_calls: number;
		failed_model_calls: number;
		failure_rate_pct: number | null;
		incomplete_model_calls: number;
		incomplete_rate_pct: number | null;
	};
	comparison: IAIHealthComparison;
	incomplete_comparison: IAIHealthComparison;
}

export interface IAIHealthAlert {
	code: string;
	severity: "warning" | "error" | "critical";
	service_name: string;
	model: string | null;
	operation_name: string | null;
	current_count: number;
	current_rate_pct: number | null;
	previous_count: number;
	previous_rate_pct: number | null;
	breakdown: Record<string, string | number | boolean | null>;
	thresholds: Record<string, string | number | boolean | null>;
}

export type AIWarningCategory = "incomplete_calls" | "handled_failures" | "root_down" | "root_errors"
	| "root_coverage" | "model_failures" | "missing_price" | "missing_usage";
export type AIThresholdValues = Record<string, number>;
export interface IAIThresholdOverride {
	model: string | null;
	service: string | null;
	reason: string | null;
	expires_at: string | null;
	active: boolean;
	incomplete_calls?: AIThresholdValues;
	handled_failures?: AIThresholdValues;
	root_down?: AIThresholdValues;
	root_errors?: AIThresholdValues;
	root_coverage?: AIThresholdValues;
	model_failures?: AIThresholdValues;
	missing_price?: AIThresholdValues;
	missing_usage?: AIThresholdValues;
}
export interface IAIThresholdConfig {
	version: number;
	valid: boolean;
	global_thresholds: Partial<Record<AIWarningCategory, AIThresholdValues>>;
	overrides: IAIThresholdOverride[];
	evaluations: {
		category: AIWarningCategory;
		service: string;
		model: string | null;
		operation: string | null;
		baseline: AIThresholdValues;
		effective: AIThresholdValues;
		source: string;
		baseline_warning: boolean;
		warning: boolean;
	}[];
	truncated?: boolean;
}

export interface IAIHealthSnapshot {
	threshold_config?: IAIThresholdConfig | null;
	status: "ok" | "degraded" | "errors" | "down" | "unknown";
	telemetry_status: "ok" | "unavailable" | "unknown";
	generated_at: string | null;
	window: {
		current_start: string | null;
		current_end: string | null;
		window_minutes: number;
		comparison_minutes: number;
	};
	service_availability: IAIHealthServiceAvailability[];
	recoverable_child_errors: IAIHealthChildError[];
	model_operations: IAIHealthModelOperation[];
	alerts: IAIHealthAlert[];
	error_type?: string | null;
	truncated?: Record<string, number>;
}

// Tenant health-check state (GET /tenants/health) — FR-13. One entry per
// tenant; a tenant never probed yet reports status "unknown".
export interface ITenantHealth {
	tenant_id: string;
	tenant_code: string;
	tenant_status: string;
	// "errors" = services reachable but a feature is failing requests (an alert,
	// not a tenant-down); "down" = a service/infra is unreachable.
	status: "up" | "errors" | "down" | "unknown";
	enabled: boolean;
	muted: boolean;
	// Not in the deployment feed yet (the Bundle column's "Not live yet"): the
	// sweep never probes it, so there is no status to show.
	not_live?: boolean;
	muted_until: string | null; // ISO datetime; snooze auto-expires server-side
	health_url: string | null; // per-tenant probe override (null = default)
	probe_url: string; // the URL the sweep actually hits (resolved server-side)
	consecutive_failures: number;
	latency_ms: number | null;
	last_error: string | null;
	// Per-service states when the tenant exposes a deep-health payload.
	components: Record<string, string> | null;
	// Sanitized AI telemetry from the latest deep check; null after a basic
	// check/fallback so stale AI results are never presented as current.
	ai_health?: IAIHealthSnapshot | null;
	last_checked_at: string | null;
	last_ok_at: string | null;
	down_since: string | null;
	last_alerted_at: string | null;
	// Whether CTPL signs its probes (KCC_HEALTH_STATUS_SECRET configured). False
	// + a deep /ctpl/health probe URL = a guaranteed 404 on the CTPL side.
	probe_signed?: boolean;
}

// Health-check settings (PUT /tenants/{tenantId}/health) — partial, exclude_unset.
export interface IHealthSettingsPayload {
	// N > 0 snoozes checks+alerts for N hours (auto-expires); 0 clears a snooze.
	snooze_hours?: number;
	enabled?: boolean;
	health_url?: string | null;
}

// POST /tenants/health/run summary.
export interface IHealthSweepSummary {
	checked: number;
	up: number;
	down: number;
	alerts_sent: number;
	recoveries_sent: number;
	skipped: number;
	failed: number;
}

// Editable tenant fields (PATCH /tenants/{tenantId}) — Overview inline edit. Only
// the fields present are applied server-side (exclude_unset).
export interface ITenantUpdatePayload {
	// Code is an editable deployment attribute; immutable UUID remains the route
	// identity. The backend only 409s on a duplicate code+environment+URL tuple.
	tenant_code?: string;
	// When sent, still at least one module.
	modules?: TenantModule[];
	cst_poc_name?: string;
	cst_poc_email?: string;
	kst_poc_name?: string;
	kst_poc_email?: string;
	project_code?: string;
	expiry_date?: string | null;
	// Correctable (seeded values are approximations); backend 422s a future date.
	onboarded_at?: string | null;
	tenant_url?: string;
	// Budget / promised cover (FR-14): null clears (disables utilization).
	promised_cover?: number | null;
	cover_start?: string | null;
	cover_end?: string | null;
	utilization_alert_pct?: number | null;
}

// A provisioning email KCC drafts for the cloud team (FR-4 / FR-5). KCC does not
// send mail — the operator reviews/copies/sends it.
export interface IDraftedEmail {
	subject: string;
	to: string[];
	cc: string[];
	body: string;
}

// Decommission request (FR-5). KCC initiates + tracks; the cloud team approves
// and executes the purge over email — there is no in-app approval step.
export type DecommissionStatus = "requested" | "cancelled";

export interface IDecommissionRequest {
	id: string;
	tenant_code: string;
	reason: string | null;
	requested_by: string;
	requested_at: string;
	status: DecommissionStatus;
	drafted_email?: IDraftedEmail; // present on the POST that opens the request
}
